"use client";

import * as React from "react";
import { Check, Loader2, Minus, Pencil, Plus, Sparkles } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { TooltipProvider } from "~/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";
import { formatHirePrice } from "~/lib/marketplace/hire-pricing";
import {
  RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS,
  RECOMMENDATIONS_LOCATION_MONTHLY_CENTS,
  RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS,
  computeDepartmentCostCents,
  computeEffectiveTeamMemberCount,
  computeLocationCostCents,
  computeTeamMemberCostCents,
  type HireExtraTeamMember,
  type HireRecommendationsSelection,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { HirePricingTeamContact } from "~/components/marketplace/hire-recommendations-step";
import { InfoTip } from "~/components/marketplace/hire-recommendations-step";
import {
  HireAddLocationDialog,
  HireLocationDetailCard,
} from "~/components/marketplace/hire-add-location-dialog";
import {
  HireAddDepartmentDialog,
  HireDepartmentDetailCard,
} from "~/components/marketplace/hire-add-department-dialog";
import {
  HireTeamMemberDialog,
  type HireTeamMemberFormValues,
} from "~/components/marketplace/hire-team-member-dialog";
import { formatHireLocationLabel } from "~/lib/marketplace/hire-location-utils";
import {
  buildHireTeamRoster,
  extraMemberToFormValues,
  formValuesToExtraMember,
  memberDisplayName,
  splitDisplayName,
  type HireTeamRosterEntry,
} from "~/lib/marketplace/hire-team-roster";

type HireCompanyProfilePricingSectionProps = {
  selection: HireRecommendationsSelection;
  onChange: (next: HireRecommendationsSelection) => void;
  className?: string;
  employerContact?: HirePricingTeamContact;
  leadTeamMember?: HirePricingTeamContact;
  employerFields?: HireTeamMemberFormValues;
  leadFields?: HireTeamMemberFormValues;
  onEmployerUpdate?: (values: HireTeamMemberFormValues) => void;
  onLeadUpdate?: (values: HireTeamMemberFormValues) => void;
  /** Limit visible blocks (wizard organization step). */
  visibleSections?: Array<"team" | "locations" | "departments">;
  /** SiteGlue: hide à-la-carte $/seat pricing; use Pricing Plans in the header instead. */
  hideUnitPricing?: boolean;
  onGenerateLocations?: () => void;
  onGenerateDepartments?: () => void;
  isGeneratingLocations?: boolean;
  isGeneratingDepartments?: boolean;
};

const DEFAULT_TEAM_MEMBER_IMAGE =
  "https://qzmrh6hprwvcouzh.public.blob.vercel-storage.com/icon-55H0ZQhxxToq0QGzxl0GwE1lgNeElP.jpeg";

type TeamMemberCardMember = {
  name: string;
  email?: string;
  title?: string;
  phone?: string;
  imageUrl?: string;
};

function TeamMemberCard({
  member,
  onEdit,
}: {
  member: TeamMemberCardMember;
  onEdit: () => void;
}) {
  const imageUrl = member.imageUrl?.trim() || DEFAULT_TEAM_MEMBER_IMAGE;

  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-slate-100 bg-white px-3 py-2">
      <div className="flex min-w-0 items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0 border border-slate-200">
          <AvatarImage src={imageUrl} alt={member.name} className="object-cover" />
          <AvatarFallback>{(member.name || "TM").slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{member.name || "—"}</p>
          {member.title?.trim() ? (
            <p className="text-xs text-slate-600">{member.title.trim()}</p>
          ) : null}
          {member.phone?.trim() ? (
            <p className="text-xs text-slate-500">{member.phone.trim()}</p>
          ) : null}
          {member.email?.trim() ? (
            <p className="text-xs text-slate-500">{member.email.trim()}</p>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 shrink-0 px-2 text-primary"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEdit();
        }}
      >
        <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
        Edit
      </Button>
    </div>
  );
}

export function HireCompanyProfilePricingSection({
  selection,
  onChange,
  className,
  employerContact,
  leadTeamMember,
  employerFields,
  leadFields,
  onEmployerUpdate,
  onLeadUpdate,
  visibleSections,
  hideUnitPricing = false,
  onGenerateLocations,
  onGenerateDepartments,
  isGeneratingLocations = false,
  isGeneratingDepartments = false,
}: HireCompanyProfilePricingSectionProps) {
  const showTeam = !visibleSections || visibleSections.includes("team");
  const showLocations = !visibleSections || visibleSections.includes("locations");
  const showDepartments = !visibleSections || visibleSections.includes("departments");
  const [employerDialogOpen, setEmployerDialogOpen] = React.useState(false);
  const [leadDialogOpen, setLeadDialogOpen] = React.useState(false);
  const [addTeamDialogOpen, setAddTeamDialogOpen] = React.useState(false);
  const [addLocationDialogOpen, setAddLocationDialogOpen] = React.useState(false);
  const [editingExtraIndex, setEditingExtraIndex] = React.useState<number | null>(null);
  const [editingLocationIndex, setEditingLocationIndex] = React.useState<number | null>(null);
  const [editingDepartmentIndex, setEditingDepartmentIndex] = React.useState<number | null>(null);
  const [addDepartmentDialogOpen, setAddDepartmentDialogOpen] = React.useState(false);

  const locationCostCents = computeLocationCostCents(selection.locationCount);
  const departmentCostCents = computeDepartmentCostCents(selection.departmentCount);
  const teamCostCents = computeTeamMemberCostCents(selection.teamMemberCount);

  const activeLocationDetails = selection.locationDetails.slice(0, selection.locationCount);
  const activeDepartmentDetails = selection.departmentDetails.slice(0, selection.departmentCount);

  const teamRoster = React.useMemo(
    () =>
      buildHireTeamRoster({
        employer: employerFields ?? null,
        lead: leadFields ?? null,
        extras: selection.extraTeamMembers,
      }),
    [employerFields, leadFields, selection.extraTeamMembers],
  );

  const defaultLeadEmail =
    leadFields?.email.trim() ||
    employerFields?.email.trim() ||
    teamRoster.find((entry) => entry.role === "lead")?.email ||
    teamRoster.find((entry) => entry.role === "employer")?.email ||
    teamRoster[0]?.email;

  const update = (patch: Partial<HireRecommendationsSelection>) => {
    onChange({ ...selection, ...patch });
  };

  const syncTeamCount = (extras: HireExtraTeamMember[]) => {
    const employerEmail = employerFields?.email ?? employerContact?.email ?? "";
    const leadEmail = leadFields?.email ?? leadTeamMember?.email ?? employerEmail;
    return computeEffectiveTeamMemberCount({
      employerEmail,
      leadEmail,
      extraCount: extras.length,
    });
  };

  const handleRemoveTeamMember = () => {
    const extras = [...selection.extraTeamMembers];
    if (extras.length > 0) {
      extras.pop();
      update({
        extraTeamMembers: extras,
        teamMemberCount: syncTeamCount(extras),
      });
      return;
    }

    if (selection.teamMemberCount <= 1) return;
    update({ teamMemberCount: selection.teamMemberCount - 1 });
  };

  const handleSaveExtraTeamMember = (values: HireTeamMemberFormValues) => {
    const member = formValuesToExtraMember(values);
    const extras = [...selection.extraTeamMembers];

    if (editingExtraIndex != null) {
      extras[editingExtraIndex] = member;
    } else {
      extras.push(member);
    }

    update({
      extraTeamMembers: extras,
      teamMemberCount: syncTeamCount(extras),
    });
    setEditingExtraIndex(null);
  };

  const handleRemoveLocation = () => {
    if (selection.locationCount <= 1) return;
    const nextCount = selection.locationCount - 1;
    update({
      locationCount: nextCount,
      locationList: selection.locationList.slice(0, nextCount),
      locationDetails: selection.locationDetails.slice(0, nextCount),
    });
  };

  const handleAddDepartment = (department: (typeof selection.departmentDetails)[number]) => {
    const nextDetails = [...selection.departmentDetails, department];
    update({
      departmentCount: nextDetails.length,
      departmentDetails: nextDetails,
    });
  };

  const handleEditDepartment = (
    index: number,
    department: (typeof selection.departmentDetails)[number],
  ) => {
    const nextDetails = [...selection.departmentDetails];
    nextDetails[index] = department;
    update({
      departmentDetails: nextDetails,
    });
    setEditingDepartmentIndex(null);
  };

  const handleRemoveDepartment = () => {
    if (selection.departmentCount <= 1) return;
    const nextCount = selection.departmentCount - 1;
    update({
      departmentCount: nextCount,
      departmentDetails: selection.departmentDetails.slice(0, nextCount),
    });
  };

  const openAddDepartmentDialog = () => {
    setEditingDepartmentIndex(null);
    setAddDepartmentDialogOpen(true);
  };

  const openEditDepartment = (index: number) => {
    setEditingDepartmentIndex(index);
    setAddDepartmentDialogOpen(true);
  };

  const handleAddLocation = (location: (typeof selection.locationDetails)[number]) => {
    const nextDetails = [...selection.locationDetails, location];
    const nextList = [...selection.locationList, formatHireLocationLabel(location)];
    update({
      locationCount: nextDetails.length,
      locationDetails: nextDetails,
      locationList: nextList,
    });
  };

  const handleEditLocation = (
    index: number,
    location: (typeof selection.locationDetails)[number],
  ) => {
    const nextDetails = [...selection.locationDetails];
    nextDetails[index] = location;
    const nextList = [...selection.locationList];
    nextList[index] = formatHireLocationLabel(location);
    update({
      locationDetails: nextDetails,
      locationList: nextList,
    });
    setEditingLocationIndex(null);
  };

  const openEditExtra = (index: number) => {
    setEditingExtraIndex(index);
    setAddTeamDialogOpen(true);
  };

  const openEditLocation = (index: number) => {
    setEditingLocationIndex(index);
    setAddLocationDialogOpen(true);
  };

  const extraDialogInitialValues =
    editingExtraIndex != null
      ? extraMemberToFormValues(selection.extraTeamMembers[editingExtraIndex]!)
      : { displayName: "", firstName: "", lastName: "", email: "", title: "", phone: "", imageUrl: "" };

  const locationDialogInitial =
    editingLocationIndex != null
      ? selection.locationDetails[editingLocationIndex] ?? null
      : null;

  const visibleTeamMembers = React.useMemo(() => {
    const members: Array<{ key: string; member: TeamMemberCardMember; onEdit: () => void }> = [];

    if (leadFields?.email.trim()) {
      members.push({
        key: `lead-${leadFields.email}`,
        member: {
          name: memberDisplayName(leadFields),
          email: leadFields.email,
          title: leadFields.title,
          phone: leadFields.phone,
          imageUrl: leadFields.imageUrl,
        },
        onEdit: () => setLeadDialogOpen(true),
      });
    }

    selection.extraTeamMembers.forEach((extra, index) => {
      members.push({
        key: `extra-${extra.email}-${index}`,
        member: {
          name: extra.name,
          email: extra.email,
          title: extra.title,
          phone: extra.phone,
          imageUrl: extra.imageUrl,
        },
        onEdit: () => openEditExtra(index),
      });
    });

    return members;
  }, [leadFields, selection.extraTeamMembers]);

  return (
    <>
      <TooltipProvider>
      <div className={cn("space-y-5", className)}>
        {showTeam ? (
        <div>
          <h4 className="inline-flex items-center text-sm font-semibold text-foreground">
            Team Members
            <InfoTip
              text={
                hideUnitPricing
                  ? "People who receive leads, handoffs, and conversation summaries. Pricing Plans (header) reflect your selected Intern / Associate / Professional plan."
                  : "People who receive leads, handoffs, and conversation summaries. Your account owner and first lead team member are included free."
              }
            />
          </h4>
          {!hideUnitPricing ? (
            <p className="mt-1 text-xs text-muted-foreground">
              First team member included free, then{" "}
              {formatHirePrice(RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS)}/member/mo
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Add teammates who should receive conversation reports. Plan pricing is shown in
              Pricing Plans (header) — not as per-seat add-ons.
            </p>
          )}
          <div className="mt-3 flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleRemoveTeamMember}
                disabled={selection.teamMemberCount <= 1}
                aria-label="Decrease team members"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                {selection.teamMemberCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  setEditingExtraIndex(null);
                  setAddTeamDialogOpen(true);
                }}
                aria-label="Add team member"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {!hideUnitPricing ? (
              <span className="text-sm font-medium text-slate-700">
                {teamCostCents === 0 ? "Free" : `${formatHirePrice(teamCostCents)}/mo`}
              </span>
            ) : null}
          </div>
          {visibleTeamMembers.length > 0 ? (
            <div className="mt-3 space-y-2">
              {visibleTeamMembers.map(({ key, member, onEdit }) => (
                <TeamMemberCard key={key} member={member} onEdit={onEdit} />
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">{selection.teamMembersLabel}</p>
          )}
        </div>
        ) : null}

        {showLocations ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="inline-flex items-center text-sm font-semibold text-foreground">
              Locations
              <InfoTip text="Physical offices or service areas your agent supports for hours, transfers, and location-specific handoffs. Your first location is included free." />
            </h4>
            {onGenerateLocations ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                disabled={isGeneratingLocations || isGeneratingDepartments}
                onClick={onGenerateLocations}
              >
                {isGeneratingLocations ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                )}
                Generate with AI
              </Button>
            ) : null}
          </div>
          {!hideUnitPricing ? (
            <p className="mt-1 text-xs text-muted-foreground">
              First location included free, then{" "}
              {formatHirePrice(RECOMMENDATIONS_LOCATION_MONTHLY_CENTS)}/location/mo
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Configure the locations your AI Employee supports.
            </p>
          )}
          <div className="mt-3 flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleRemoveLocation}
                disabled={selection.locationCount <= 1}
                aria-label="Decrease locations"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                {selection.locationCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  setEditingLocationIndex(null);
                  setAddLocationDialogOpen(true);
                }}
                aria-label="Add location"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {!hideUnitPricing ? (
              <span className="text-sm font-medium text-slate-700">
                {locationCostCents === 0 ? "Free" : `${formatHirePrice(locationCostCents)}/mo`}
              </span>
            ) : null}
          </div>
          {activeLocationDetails.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {activeLocationDetails.map((loc, index) => (
                <li key={`${loc.name}-${index}`} className="flex items-start gap-2">
                  <Check className="mt-2 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <HireLocationDetailCard
                      detail={loc}
                      teamRoster={teamRoster}
                      onEdit={() => openEditLocation(index)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : selection.locationList.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {selection.locationList.slice(0, selection.locationCount).map((loc) => (
                <li key={loc} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{loc}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50/80 px-3 py-3">
              <p className="text-sm text-slate-600">No locations found yet.</p>
              <p className="mt-1 text-xs text-slate-500">
                {onGenerateLocations
                  ? "Click Generate with AI to pull offices from your website, or add a location manually."
                  : "Add a location manually with the + button."}
              </p>
            </div>
          )}
        </div>
        ) : null}

        {showDepartments ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="inline-flex items-center text-sm font-semibold text-foreground">
              Departments
              <InfoTip text="Business departments your agent can route conversations to, such as billing, scheduling, or clinical teams. Your first department is included free." />
            </h4>
            {onGenerateDepartments ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                disabled={isGeneratingDepartments || isGeneratingLocations}
                onClick={onGenerateDepartments}
              >
                {isGeneratingDepartments ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                )}
                Generate with AI
              </Button>
            ) : null}
          </div>
          {!hideUnitPricing ? (
            <p className="mt-1 text-xs text-muted-foreground">
              First department included free, then{" "}
              {formatHirePrice(RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS)}/department/mo
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Configure departments your AI Employee can route to.
            </p>
          )}
          <div className="mt-3 flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() =>
                  handleRemoveDepartment()
                }
                disabled={selection.departmentCount <= 1}
                aria-label="Decrease departments"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                {selection.departmentCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={openAddDepartmentDialog}
                aria-label="Add department"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {!hideUnitPricing ? (
              <span className="text-sm font-medium text-slate-700">
                {departmentCostCents === 0
                  ? "Free"
                  : `${formatHirePrice(departmentCostCents)}/mo`}
              </span>
            ) : null}
          </div>
          {activeDepartmentDetails.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {activeDepartmentDetails.map((department, index) => (
                <li key={`${department.name}-${index}`} className="flex items-start gap-2">
                  <Check className="mt-2 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <HireDepartmentDetailCard
                      detail={department}
                      teamRoster={teamRoster}
                      onEdit={() => openEditDepartment(index)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        ) : null}
      </div>
      </TooltipProvider>

      <HireAddDepartmentDialog
        open={addDepartmentDialogOpen}
        onOpenChange={(open) => {
          setAddDepartmentDialogOpen(open);
          if (!open) setEditingDepartmentIndex(null);
        }}
        teamRoster={teamRoster}
        defaultLeadEmail={defaultLeadEmail}
        initialValues={
          editingDepartmentIndex != null
            ? activeDepartmentDetails[editingDepartmentIndex]
            : undefined
        }
        editIndex={editingDepartmentIndex}
        onAdd={handleAddDepartment}
        onEdit={handleEditDepartment}
      />

      <HireTeamMemberDialog
        open={employerDialogOpen}
        onOpenChange={setEmployerDialogOpen}
        title="Edit Employer"
        description="Update the account owner for this Inbound GPT agent."
        initialValues={
          employerFields ?? {
            displayName: employerContact?.name ?? "",
            firstName: "",
            lastName: "",
            email: employerContact?.email ?? "",
            title: "",
            phone: "",
            imageUrl: "",
          }
        }
        onSave={(values) => onEmployerUpdate?.(values)}
        submitLabel="Save"
      />

      <HireTeamMemberDialog
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        title="Edit Lead Team Member"
        description="Update the lead team member who receives conversation summaries and handoffs."
        initialValues={
          leadFields ?? {
            displayName: leadTeamMember?.name ?? "",
            firstName: "",
            lastName: "",
            email: leadTeamMember?.email ?? "",
            title: "",
            phone: "",
            imageUrl: "",
          }
        }
        onSave={(values) => onLeadUpdate?.(values)}
        submitLabel="Save"
      />

      <HireTeamMemberDialog
        open={addTeamDialogOpen}
        onOpenChange={(open) => {
          setAddTeamDialogOpen(open);
          if (!open) setEditingExtraIndex(null);
        }}
        title={editingExtraIndex != null ? "Edit Team Member" : "Add Team Member"}
        description="Add business profile for customer-facing interactions"
        initialValues={extraDialogInitialValues}
        onSave={handleSaveExtraTeamMember}
        submitLabel={editingExtraIndex != null ? "Save" : "Create Profile"}
      />

      <HireAddLocationDialog
        open={addLocationDialogOpen}
        onOpenChange={(open) => {
          setAddLocationDialogOpen(open);
          if (!open) setEditingLocationIndex(null);
        }}
        onAdd={handleAddLocation}
        onEdit={handleEditLocation}
        editIndex={editingLocationIndex}
        initialLocation={locationDialogInitial}
        teamRoster={teamRoster}
        defaultLeadEmail={defaultLeadEmail}
      />
    </>
  );
}

export function hireContactFromFormValues(values: HireTeamMemberFormValues) {
  const { firstName, lastName } = values.displayName
    ? splitDisplayName(values.displayName)
    : { firstName: values.firstName, lastName: values.lastName };
  return {
    firstName,
    lastName,
    email: values.email.trim(),
    phone: values.phone?.trim() || undefined,
    title: values.title?.trim() || undefined,
    imageUrl: values.imageUrl?.trim() || undefined,
    name: memberDisplayName(values),
  };
}

export type { HireTeamRosterEntry };
